/**
 * Plan comptable paie — Luxembourg (PCN)
 *
 * Mapping par defaut des postes de paie vers les comptes du PCN.
 * Configurable par societe — chaque entreprise peut surcharger le mapping.
 *
 * Structure PCN Luxembourg :
 * - Classe 4 : Comptes de tiers (personnel, organismes sociaux)
 * - Classe 6 : Charges (salaires, cotisations)
 *
 * Le journal de paie est en OD (Operations Diverses).
 */

export interface AccountMapping {
  /** Code du poste de paie */
  payrollItemCode: string
  /** Libelle du poste */
  label: string
  /** Compte de debit */
  debitAccount: string
  /** Compte de credit */
  creditAccount: string
  /** Categorie pour le regroupement */
  category: 'remuneration' | 'cotisation_salariale' | 'cotisation_patronale' | 'impot' | 'provision' | 'net'
}

export interface ChartOfAccounts {
  /** Identifiant de la configuration */
  id: string
  /** Nom de la societe */
  companyName: string
  /** Mappings poste → comptes */
  mappings: AccountMapping[]
  /** Journal par defaut */
  defaultJournal: string
}

/**
 * Plan comptable paie par defaut — PCN Luxembourg
 *
 * Comptes utilises :
 * 6410xx — Remunerations brutes
 * 6450xx — Cotisations patronales
 * 421xxx — Personnel, remunerations dues
 * 431xxx — CCSS cotisations salariales
 * 432xxx — CCSS cotisations patronales
 * 441xxx — Etat, impots sur salaires (RTS)
 * 428xxx — Autres dettes envers le personnel
 */
export const DEFAULT_PCN_MAPPINGS: AccountMapping[] = [
  // ─── Remunerations (charges) ───────────────────────────────
  {
    payrollItemCode: 'SALAIRE_BASE',
    label: 'Salaires de base',
    debitAccount: '641100',
    creditAccount: '421100',
    category: 'remuneration',
  },
  {
    payrollItemCode: 'HEURES_SUP',
    label: 'Heures supplementaires',
    debitAccount: '641200',
    creditAccount: '421100',
    category: 'remuneration',
  },
  {
    payrollItemCode: 'TREIZIEME_MOIS',
    label: '13eme mois',
    debitAccount: '641300',
    creditAccount: '421100',
    category: 'remuneration',
  },
  {
    payrollItemCode: 'AVANTAGE_NATURE',
    label: 'Avantages en nature',
    debitAccount: '641400',
    creditAccount: '421100',
    category: 'remuneration',
  },

  // ─── Cotisations salariales (retenues) ─────────────────────
  {
    payrollItemCode: 'CSS_MALADIE_SAL',
    label: 'Cotisation maladie salariale',
    debitAccount: '421100',
    creditAccount: '431100',
    category: 'cotisation_salariale',
  },
  {
    payrollItemCode: 'CSS_PENSION_SAL',
    label: 'Cotisation pension salariale',
    debitAccount: '421100',
    creditAccount: '431200',
    category: 'cotisation_salariale',
  },
  {
    payrollItemCode: 'CSS_DEPENDANCE',
    label: 'Cotisation dependance',
    debitAccount: '421100',
    creditAccount: '431300',
    category: 'cotisation_salariale',
  },

  // ─── Impot (retenue) ──────────────────────────────────────
  {
    payrollItemCode: 'RTS',
    label: 'Retenue impot sur salaires',
    debitAccount: '421100',
    creditAccount: '441100',
    category: 'impot',
  },

  // ─── Cotisations patronales (charges) ──────────────────────
  {
    payrollItemCode: 'CSS_MALADIE_PAT',
    label: 'Cotisation maladie patronale',
    debitAccount: '645100',
    creditAccount: '432100',
    category: 'cotisation_patronale',
  },
  {
    payrollItemCode: 'CSS_PENSION_PAT',
    label: 'Cotisation pension patronale',
    debitAccount: '645200',
    creditAccount: '432200',
    category: 'cotisation_patronale',
  },
  {
    payrollItemCode: 'CSS_ACCIDENT',
    label: 'Cotisation accident du travail',
    debitAccount: '645300',
    creditAccount: '432300',
    category: 'cotisation_patronale',
  },
  {
    payrollItemCode: 'CSS_MUTUALITE',
    label: 'Cotisation mutualite',
    debitAccount: '645400',
    creditAccount: '432400',
    category: 'cotisation_patronale',
  },
  {
    payrollItemCode: 'CSS_SANTE',
    label: 'Cotisation sante au travail',
    debitAccount: '645500',
    creditAccount: '432500',
    category: 'cotisation_patronale',
  },

  // ─── Net a payer ──────────────────────────────────────────
  {
    payrollItemCode: 'NET_A_PAYER',
    label: 'Net a payer aux salaries',
    debitAccount: '421100',
    creditAccount: '512000',
    category: 'net',
  },

  // ─── Cheques-repas ────────────────────────────────────────
  {
    payrollItemCode: 'CHEQUES_REPAS',
    label: 'Cheques-repas (part employeur)',
    debitAccount: '647100',
    creditAccount: '428100',
    category: 'remuneration',
  },

  // ─── Provisions ───────────────────────────────────────────
  {
    payrollItemCode: 'PROV_CONGES',
    label: 'Provision conges payes',
    debitAccount: '641500',
    creditAccount: '428200',
    category: 'provision',
  },
  {
    payrollItemCode: 'PROV_13MOIS',
    label: 'Provision 13eme mois',
    debitAccount: '641300',
    creditAccount: '428300',
    category: 'provision',
  },
]

export const DEFAULT_CHART: ChartOfAccounts = {
  id: 'pcn-lu-default',
  companyName: '',
  mappings: DEFAULT_PCN_MAPPINGS,
  defaultJournal: 'OD',
}
